import { isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, HostListener, Inject, OnInit, PLATFORM_ID, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Functions } from 'src/app/helpers/Functions';
import { CommunityService } from 'src/app/services/community/community.service';
import { TicketDTO } from 'src/app/DTO/TicketDTO';
import { environment } from 'src/environments/environment';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DateTime } from 'luxon';
import { atcb_action } from "add-to-calendar-button";
import { TranslateService } from '@ngx-translate/core';
import { DateIsBetweenResult, Helpers } from 'src/app/helpers';
import { ScreenSizeService } from 'src/app/services/screen-size.service';
import { LocalizedDatePipe } from 'src/app/pipes/localized-date.pipe';
import { EsdevenimentDTO } from 'src/app/DTO/EsdevenimentDTO';
import { CommunityDTO } from 'src/app/DTO/CommunityDTO';
import { ApiService } from 'src/app/services/api.service';
import { MetadataService } from 'src/app/services/metadata.service';
import { TicketsService } from 'src/app/pages/events/events-detail/payment-modal/services/tickets.service';
import { PaymentModalComponent } from 'src/app/pages/events/events-detail/payment-modal/payment-modal.component';
import { ImageUrlPipe } from 'src/app/pipes/image-url.pipe';
import { firstValueFrom } from 'rxjs';
import { DetailDTO } from 'src/app/DTO/DetailDTO';
import { InscriptionsConstants } from "../../../inscriptions/constants";
import { TicketWaitingListInfoDTO } from 'src/app/DTO/tickets/TicketWaitingListInfoDTO';
import { TicketFreePlacesDTO } from 'src/app/DTO/tickets/TicketFreePlacesDTO';
import { WaitingListTicketComponent } from '../waiting-list-ticket/waiting-list-ticket.component';
import Swal from 'sweetalert2';
import { ticketI18nKey, ticketingExceededI18nKey } from 'src/app/helpers/ticket-translate.helper';

@Component({
  selector: 'app-ticket-detail',
  templateUrl: './ticket-detail.component.html',
  styleUrls: ['./ticket-detail.component.scss']
})
export class TicketDetailComponent implements OnInit {

  @ViewChild('addCalendar') addCalendar: ElementRef;
  @ViewChild('addCalendarBig') addCalendarBig: ElementRef;
  public OpenStatuses = InscriptionsConstants.OpenStatuses;
  eventId: string = "";
  // event?: EsdevenimentDTO;
  community?: CommunityDTO;
  communityName: string = "";

  public month: string;
  public day: string;
  public dateOpen: DateTime;
  public dateClose: DateTime;

  public model = signal(new DetailDTO);
  public ticket = signal(new TicketDTO);
  public event = signal(new EsdevenimentDTO);

  public dateFrom: Date;
  public dateTo: Date;
  public sameDay: boolean;

  public $loading = signal(true);

  public addToCalendarData = {};
  public canAddCalendar = true;
  public addToCalendarLoaded = false;

  public availableTickets: number | null;
  public availabilityFreePlaces: TicketFreePlacesDTO | null = null;
  public isFull = false;
  public accessCode: string | null = null;
  public waitingListUserDetails: TicketWaitingListInfoDTO | null = null;

  public defaultImg: string;
  public isMobile: boolean;
  public hideBack = false;
  public isCustom: boolean;
  public openStatus: string;

  public buyButtonState: { enabled: boolean; text: string };

  private ref: DynamicDialogRef | undefined;
  private customCommunities = [
    'tortola-de-henares'
  ];

  faArrowLeft = faArrowLeft;

  @HostListener('window:resize') onresize() {
    this.isMobile = this.screenSizeService.isScreenSmall();
  }


  constructor(
    private apiService: ApiService,
    private actRoute: ActivatedRoute,
    private titleService: Title,
    private metadataService: MetadataService,
    private communityService: CommunityService,
    private ticketsService: TicketsService,
    private dialogService: DialogService,
    private translate: TranslateService,
    private screenSizeService: ScreenSizeService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {

    this.eventId = this.actRoute.snapshot.params['id'];
    this.communityName = this.actRoute.snapshot.params['community'];
    this.accessCode = this.actRoute.snapshot.queryParamMap.get('AccessCode');

    this.isCustom = this.customCommunities.includes(this.communityName);

    if (this.actRoute.snapshot.queryParams['hideBack'] != undefined) {
      this.hideBack = true;
    }
    if (this.accessCode && this.eventId) {
      const ticketId = Number(this.eventId);
      this.apiService.checkTicketAccessCode(this.communityName, { ticketId, accessCode: this.accessCode }).subscribe({
        next: (x) => {
          this.waitingListUserDetails = x;
          this.waitingListUserDetails.accessCode = this.accessCode;
          this.isFull = false;
          const cancel = this.actRoute.snapshot.queryParamMap.get('cancel');
          if (cancel) {
            this.confirmRejectWaitingList();
          }
        },
        error: (err) => this.handleWaitingListError(err)
      });
    }

    this.buyButtonState = {
      enabled: false,
      text: this.translate.instant(ticketI18nKey(this.communityName, 'ReserveTicket'))
    }
  }

  public exceededKey(): string {
    return ticketingExceededI18nKey(this.communityName);
  }

  ngOnInit() {
    this.isMobile = this.screenSizeService.isScreenSmall();
    this.defaultImg = this.communityService.defaultImg;
    this.init();
  }

  private requiresAppRedirect(ticket: TicketDTO): boolean {
    const hasValidationRule = ticket.validationRule != null && ticket.validationRule !== 'None';
    return hasValidationRule
      || ticket.minimumAge != null
      || ticket.maximumAge != null
      || !!ticket.ticketsGroups?.length
      || !!ticket.queueItWaitingRoomId?.trim();
  }

  private buildAppTicketUrl(ticketId: number): string {
    const appHost = environment.production ? 'app.wetown.app' : `app.${environment.tag}.wetown.app`;
    const params = new URLSearchParams({ web: '1' });
    if (this.accessCode) {
      params.set('AccessCode', this.accessCode);
    }
    return `https://${appHost}/c/${this.communityName}/tickets/${ticketId}?${params}`;
  }

  private redirectToAppTicketIfNeeded(ticket: TicketDTO): boolean {
    if (!this.requiresAppRedirect(ticket) || !isPlatformBrowser(this.platformId)) {
      return false;
    }

    window.location.href = this.buildAppTicketUrl(ticket.id);
    return true;
  }

  private getCommunity() {
    return firstValueFrom(this.apiService.getCommunityByCode(this.communityName));
  }

  ngOnDestroy(): void {
  }

  public openTicketModal() {
    const width = this.isMobile ? '90%' : '700px';
    this.ref = this.dialogService.open(PaymentModalComponent, {
      closable: true,
      position: 'center',
      styleClass: 'header-transparent',
      baseZIndex: 1005,
      data: { ticket: this.ticket(), waitingListUserDetails: this.waitingListUserDetails ?? undefined },
      width: width,
    });
    this.ref.onClose.subscribe((result: { clearAccessCode?: boolean }) => {
      if (result?.clearAccessCode) {
        this.cleanAccesCodeWaitingList();
        this.refreshAvailability();
        this.refreshButtonState();
      }
    });
  }

  public confirmRejectWaitingList() {
    if (!this.waitingListUserDetails) return;
    Swal.fire({
      icon: 'info',
      title: this.translate.instant('WAITING_LIST.CONFIRM_EXIT_TITLE'),
      text: this.translate.instant('WAITING_LIST.CONFIRM_EXIT_MESSAGE_TICKETS'),
      showCancelButton: true,
      confirmButtonText: this.translate.instant('WAITING_LIST.CANCEL_TICKETS'),
      cancelButtonText: this.translate.instant('WAITING_LIST.CONFIRM'),
      customClass: { confirmButton: 'swal-confirm-corp' },
      reverseButtons: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: () => {
        Swal.showLoading();
        return firstValueFrom(
          this.apiService.rejectFromTicketWaitingList(this.communityName, this.waitingListUserDetails!.id)
        ).catch((err) => {
          Swal.showValidationMessage(err?.error?.message || this.translate.instant('Error'));
          return Promise.reject(err);
        });
      }
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: 'info',
          title: this.translate.instant('WAITING_LIST.CANCELLED_TITLE'),
          text: this.translate.instant('WAITING_LIST.CANCELLED_MESSAGE_TICKETS'),
          confirmButtonText: this.translate.instant('Accept'),
          customClass: { confirmButton: 'swal-confirm-corp' },
        });
        this.cleanAccesCodeWaitingList();
      }
    });
  }

  public rejectWaitingList() {
    if (!this.waitingListUserDetails) return;
    this.apiService.rejectFromTicketWaitingList(this.communityName, this.waitingListUserDetails.id).subscribe({
      next: () => {
        Swal.fire({
          icon: 'info',
          title: this.translate.instant('WAITING_LIST.CANCELLED_TITLE'),
          text: this.translate.instant('WAITING_LIST.CANCELLED_MESSAGE_TICKETS'),
          confirmButtonText: this.translate.instant('Accept'),
          customClass: { confirmButton: 'swal-confirm-corp' },
        });
        this.cleanAccesCodeWaitingList();
      }
    });
  }

  public cleanAccesCodeWaitingList() {
    this.router.navigate([], {
      relativeTo: this.actRoute,
      queryParams: { AccessCode: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
    this.waitingListUserDetails = null;
    this.accessCode = null;
  }

  private handleWaitingListError(err: any) {
    const code = err?.error?.code;
    let titleKey = '';
    let textKey = '';
    switch (code) {
      case 'EXPIRED_ACCESS':
        titleKey = 'WAITINGLIST.ExpiredTitle';
        textKey = 'WAITINGLIST.ExpiredSubtitle';
        break;
      case 'INVALID_ACCESS':
        titleKey = 'WAITINGLIST.InvalidTitle';
        textKey = 'WAITINGLIST.InvalidSubtitle';
        break;
      default:
        titleKey = 'WAITINGLIST.GenericErrorTitle';
        textKey = 'WAITINGLIST.GenericErrorSubtitle';
        break;
    }
    Swal.fire({
      title: this.translate.instant(titleKey),
      text: this.translate.instant(textKey),
      icon: 'error',
      confirmButtonText: this.translate.instant('Accept'),
      customClass: { confirmButton: 'swal-confirm-corp' },
    }).then(() => {
      this.waitingListUserDetails = null;
      this.accessCode = null;
      this.router.navigate([], {
        relativeTo: this.actRoute,
        queryParams: { AccessCode: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
      if (this.ticket()?.id) {
        this.refreshAvailability();
        this.refreshButtonState();
      }
    });
  }

  public openWaitingListForm(ticketPriceId?: number) {
    this.ref = this.dialogService.open(WaitingListTicketComponent, {
      closable: true,
      position: 'top',
      baseZIndex: 1200,
      data: { ticket: this.ticket(), ticketPriceId, communityName: this.communityName },
      header: this.translate.instant('INSCRIPTIONS.WaitingList.Title') + ': ' + this.ticket().title,
      styleClass: 'top-position-class form-modal waiting-list-modal',
      width: '1000px',
      appendTo: 'body'
    });
  }

  public openMaps() {
    window.open('https://www.google.com/maps?q=' + this.event().locationLat + ',' + this.event().locationLng);
  }

  public addToCalendar(bigBtn?: boolean) {
    atcb_action(this.addToCalendarData, bigBtn ? this.addCalendarBig.nativeElement : this.addCalendar.nativeElement)
  }

  public downloadTicket(uuid: string) {
    this.router.navigateByUrl(`/${this.community.code}/tickets/print/${uuid}`);
  }

  private async init() {
    this.community = await this.getCommunity();

    this.getTicket()
      .subscribe((ticket) => {
        this.setTicket(ticket)
      });
  }

  private getTicket() {
      const id = this.actRoute.snapshot.params['id'];
      return this.ticketsService
        .getTicketById(this.community.id, id);
  }

  private async setTicket(ticket: TicketDTO) {
    if (this.redirectToAppTicketIfNeeded(ticket)) {
      return;
    }

    this.ticket.set(ticket);

    if(ticket.eventId) {
      const event = await this.getEvent();
      this.event.set(event);
    }

    this.dateOpen = DateTime.fromISO(this.ticket().saleDateFrom);
    this.dateClose = DateTime.fromISO(this.ticket().saleDateTo);

    if (this.dateOpen <= DateTime.now() && this.dateClose > DateTime.now()){
      this.openStatus = InscriptionsConstants.OpenStatuses.OPEN;
    } else if (this.dateOpen > DateTime.now()){
      this.openStatus = InscriptionsConstants.OpenStatuses.FUTURE;
    } else if (this.dateClose < DateTime.now()){
      this.openStatus = InscriptionsConstants.OpenStatuses.PAST;
    }

    const detail = this.setDetail();

    if(detail.startEvent && detail.endEvent) {
      this.sameDay = DateTime.fromJSDate(detail.startEvent).hasSame(DateTime.fromJSDate(detail.endEvent), 'day');
    }

    if(detail.image && detail.name) {
      this.titleService.setTitle(detail.name);

      const absoluteImageUrl = new ImageUrlPipe().transform(detail.image);
      this.metadataService.updateMetadata({
        title: Functions.decode(detail.name),
        description: Functions.decode(detail.summary),
        image: absoluteImageUrl ? Functions.decode(absoluteImageUrl) : null
      });
    }

    this.model.set(
      detail
    );

    this.setCalendarButton();
    this.refreshAvailability();
    this.refreshButtonState();

    this.$loading.set(false);
  }

  private getEvent() {
    return firstValueFrom(this.apiService.getEsdevenimentById(this.community.code, this.ticket().eventId));
  }

  private setDetail(): DetailDTO {
    if(this.ticket().eventId) {
      return {
        name: this.event().name,
        description: this.event().description,
        image: this.event().image,
        images: this.event().images,
        dateFrom: DateTime.fromISO(this.event().dateFrom.toString()).toJSDate(),
        startEvent: DateTime.fromISO(this.event().dateFrom.toString()).toJSDate(),
        dateTo: this.event().dateTo ? DateTime.fromISO(this.event().dateTo.toString()).toJSDate() : null,
        endEvent: this.event().dateTo ? DateTime.fromISO(this.event().dateTo.toString()).toJSDate() : null,
        locationString: this.event().locationString,
        locationLat: this.event().locationLat,
        locationLng: this.event().locationLng,
        files: this.event().files,
        id: this.event().id,
        ticketId: this.ticket().id,
        allDay: this.event().allDay,
        summary: this.event().summary,
        categoryName: this.event().categoryName,
        isEvent: true,
      } as DetailDTO;
    } else {
      const startEvent = this.ticket().startEvent
          ? DateTime.fromISO(this.ticket().startEvent).toJSDate()
          : null;
      const endEvent = this.ticket().endEvent
          ? DateTime.fromISO(this.ticket().endEvent).toJSDate()
          : null;
      return {
        name: this.ticket().title,
        description: this.ticket().body,
        image: this.ticket()?.images[0]?.path,
        images: this.ticket().images.map(img => img.path),
        dateFrom: startEvent,
        dateTo: endEvent,
        startEvent,
        endEvent,
        files: this.ticket().files,
        id: this.ticket().id,
        summary: this.ticket().summary,
        allDay: false,
        categoryName: this.ticket().categoryName,
        ticketId: this.ticket().id,
      } as DetailDTO;
    }
  }

  private setCalendarButton() {
    const startEvent = this.model().startEvent;
    if (!startEvent) {
      this.canAddCalendar = false;
      this.addToCalendarLoaded = false;
      return;
    }

    const name = this.model().name;
    const dateFrom = DateTime.fromJSDate(new Date(startEvent));
    const startDate = dateFrom.toISODate();
    let endDate, startTime, endTime, locationString, dateTo;
    const now = DateTime.now();
    if (this.model().endEvent != null) {
      dateTo = DateTime.fromJSDate(new Date(this.model().endEvent));
      endDate = dateTo.toISODate();
      this.canAddCalendar = dateTo > now;
    } else {
      endDate = startDate;
      this.canAddCalendar = dateFrom > now;
    }

    if (!this.model().allDay) {
      startTime = dateFrom.toFormat('hh:mm');
      if (this.model().endEvent) {
        endTime = dateTo.toFormat('hh:mm');
      } else {
        startTime = null;
        endTime = null;
      }
    } else {
      startTime = null;
      endTime = null;
    }

    if (this.model().locationString && this.model().locationString != null) {
      locationString = this.model().locationString;
    }

    this.addToCalendarData = {
      name: name,
      startDate: startDate,
      endDate: endDate,
      startTime: startTime,
      endTime: endTime,
      location: locationString,
      timeZone: 'Europe/Madrid',
      options: ['Google', 'Apple', 'Outlook.com', 'ICal'],
      inline: true,
      buttonStyle: 'round',
      customLabels: { 'close': this.translate.instant('Close') }
    }

    this.addToCalendarLoaded = true;
  }

  private refreshAvailability() {
    const ticketId = this.model().ticketId;
    const useV2 = this.ticket().waitingList || !!this.accessCode;
    if (useV2) {
      const includeWaitingList = !this.accessCode;
      this.ticketsService.getAvailabilityV2(
        this.communityName,
        ticketId,
        includeWaitingList,
        this.accessCode ?? undefined
      ).subscribe((res: TicketFreePlacesDTO) => {
        this.availabilityFreePlaces = res;
        this.availableTickets = res?.freePlaces ?? null;
        this.isFull = res?.freePlaces != null && res.freePlaces <= 0 && !this.waitingListUserDetails;
        this.refreshButtonState();
      });
    } else {
      this.ticketsService.getAvailability(this.community.id, ticketId).subscribe((res) => {
        this.availableTickets = res;
        this.availabilityFreePlaces = res != null ? { freePlaces: res, groups: [] } : null;
        this.isFull = res != null && res <= 0;
        this.refreshButtonState();
      });
    }
  }

  private refreshButtonState() {
    const availableTickets = this.availableTickets == null || this.availableTickets > 0;
    const canBuyBecauseWaitingList = !!this.waitingListUserDetails;
    const code = this.communityName;

    if (!this.ticket()) {
      if (availableTickets) {
        this.buyButtonState.text = this.translate.instant(ticketI18nKey(code, 'ReserveTicket'));
      } else {
        this.buyButtonState.text = this.translate.instant(ticketingExceededI18nKey(code));
      }
      return;
    }

    const saleStartDate = new Date(this.ticket().saleDateFrom);
    const chkDates = Helpers.CheckIfDateIsBetween(this.ticket().saleDateFrom, this.ticket().saleDateTo);
    const inTime = (chkDates == DateIsBetweenResult.Between);
    const fullFree = this.ticket().prices.every(x => x.totalWithFee == 0);

    this.buyButtonState.enabled = inTime && (availableTickets || canBuyBecauseWaitingList);

    if (inTime) {
      if (availableTickets) {
        this.buyButtonState.text = fullFree
          ? this.translate.instant(ticketI18nKey(code, 'ReserveTicket'))
          : this.translate.instant(ticketI18nKey(code, 'BuyTickets'));
      } else {
        this.buyButtonState.text = this.translate.instant(ticketingExceededI18nKey(code));
      }
    } else if (chkDates == DateIsBetweenResult.Before) {
      this.buyButtonState.text = this.translate.instant('TICKETS.SaleOpen') + ' ' + new LocalizedDatePipe(this.translate).transform(saleStartDate, 'EEEE dd MMMM HH:mm');
    } else {
      this.buyButtonState.text = this.translate.instant('TICKETS.SaleClosed');
    }

  }
}
